import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { ArrowRight, FolderOpen, Megaphone, Target, type LucideIcon } from 'lucide-react';
import { Badge, Button, Card, Skeleton } from '@bliks-io/ui';
import type { PartnerMaterial, PartnerMaterialLocale } from '@bliks-io/contracts';
import { useProfile } from '@/api/queries/useProfile';
import { useInstalledApps } from '@/api/queries/useInstalledApps';
import { findInstalledApp } from '@/lib/app-registry';
import { usePartnerNews } from '@/pages/partner/api/queries/usePartnerNews';
import { usePartnerMaterials } from '@/pages/partner/api/queries/usePartnerMaterials';
import { PartnerFileFormatIcon } from '@/components/partner-content/PartnerFileFormat';
import { PartnerInfoPanel } from '@/pages/partner/components/PartnerInfoPanel';
import { PartnerNewsStory, toNewsSlides } from '@/components/partner-content/PartnerNewsStory';
import {
  PARTNER_MATERIAL_CATEGORY_KEYS,
  isRecentlyChanged,
  pickPartnerMaterialText,
  toPartnerMaterialLocale
} from '@/lib/partner-material-text';
import { formatDate } from '@/lib/formatters';

const RECENT_MATERIAL_LIMIT = 5;

/** The sales app (SALES-68): leads and the call list live there, not in the area. */
const SALES_APP_ID = 'sales';

/** The sales app's view the home page points into (SAL-10): a path under the app (SAL-16). */
const INFO_HUB_PATH = `${SALES_APP_ID}/info-hub`;

/** Newest changes first, so the home page shows what partners have not seen yet. */
export function recentMaterials(materials: PartnerMaterial[], limit = RECENT_MATERIAL_LIMIT) {
  return [...materials]
    .sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime())
    .slice(0, limit);
}

export function PartnerHomePage() {
  const { t, i18n } = useTranslation();
  const { data: profile } = useProfile();
  const { data: installedApps } = useInstalledApps();
  const locale = toPartnerMaterialLocale(i18n.language);
  // The sales app leads the quick links for every tenant that holds it; a
  // partner without it sees no dead card.
  const hasSalesApp = !!findInstalledApp(installedApps, SALES_APP_ID);

  return (
    <div className="flex h-full flex-col gap-8 overflow-auto p-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">
          {profile?.firstName
            ? t('partner.home.greeting', { name: profile.firstName })
            : t('partner.home.greetingAnonymous')}
        </h1>
        <p className="text-sm text-muted-foreground">{t('partner.home.subtitle')}</p>
      </div>

      <nav
        aria-label={t('partner.home.quickLinks')}
        className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
      >
        {hasSalesApp && (
          <>
            <Link
              to="/partner/apps/$"
              params={{ _splat: SALES_APP_ID }}
              className={QUICK_LINK_CLASS}
            >
              <QuickLinkBody
                icon={Target}
                title={t('partner.home.links.sales.title')}
                description={t('partner.home.links.sales.description')}
              />
            </Link>
            <Link
              to="/partner/apps/$"
              params={{ _splat: INFO_HUB_PATH }}
              className={QUICK_LINK_CLASS}
            >
              <QuickLinkBody
                icon={FolderOpen}
                title={t('partner.home.links.infoHub.title')}
                description={t('partner.home.links.infoHub.description')}
              />
            </Link>
          </>
        )}
      </nav>

      <div className="grid gap-8 lg:grid-cols-3">
        <section className="flex flex-col gap-4 lg:col-span-2" aria-labelledby="partner-news">
          {/* Same row height as the other column's header, whose button sets it,
              so both cards start level. */}
          <div className="flex min-h-11 items-center">
            <h2 id="partner-news" className="text-lg font-medium text-foreground">
              {t('partner.home.news.title')}
            </h2>
          </div>
          <NewsFeed locale={locale} />
        </section>
        {/* The material lives in the sales app's Info Hub (SAL-10): a tenant
            without the app has nowhere to open it, so it shows none. */}
        {hasSalesApp && (
          <section className="flex flex-col gap-4" aria-labelledby="partner-recent-material">
            <div className="flex min-h-11 items-center justify-between">
              <h2 id="partner-recent-material" className="text-lg font-medium text-foreground">
                {t('partner.home.recentMaterial.title')}
              </h2>
              <Button variant="ghost" size="small" asChild>
                <Link to="/partner/apps/$" params={{ _splat: INFO_HUB_PATH }}>
                  {t('partner.home.recentMaterial.all')}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </div>
            <RecentMaterial locale={locale} />
          </section>
        )}
      </div>
    </div>
  );
}

const QUICK_LINK_CLASS =
  'group rounded-lg focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring';

function QuickLinkBody({
  icon: Icon,
  title,
  description
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <Card>
      <div className="flex items-start gap-3 p-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted">
          <Icon className="h-5 w-5 text-foreground" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-foreground">{title}</p>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
      </div>
    </Card>
  );
}

function NewsFeed({ locale }: { locale: PartnerMaterialLocale }) {
  const { t } = useTranslation();
  const { data: news = [], isLoading, isError } = usePartnerNews();

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (isError) {
    return <p className="text-sm text-muted-foreground">{t('partner.home.news.error')}</p>;
  }
  if (news.length === 0) {
    return (
      <Card>
        <PartnerInfoPanel icon={Megaphone} title={t('partner.home.news.empty')} className="py-10" />
      </Card>
    );
  }
  return <PartnerNewsStory slides={toNewsSlides(news)} locale={locale} />;
}

function RecentMaterial({ locale }: { locale: PartnerMaterialLocale }) {
  const { t } = useTranslation();
  const { data: materials = [], isLoading, isError } = usePartnerMaterials();

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (isError) {
    return <p className="text-sm text-muted-foreground">{t('partner.infoHub.errorDescription')}</p>;
  }
  const recent = recentMaterials(materials);
  if (recent.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('partner.infoHub.empty')}</p>;
  }
  return (
    <Card>
      <ul className="flex flex-col divide-y">
        {recent.map((material) => (
          <li key={material._id}>
            <Link
              to="/partner/apps/$"
              params={{ _splat: INFO_HUB_PATH }}
              className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50"
            >
              <PartnerFileFormatIcon format={Object.values(material.files)[0]?.format} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">
                  {pickPartnerMaterialText(material.title, locale)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t(PARTNER_MATERIAL_CATEGORY_KEYS[material.category])} ·{' '}
                  {formatDate(material.changedAt)}
                </p>
              </div>
              {isRecentlyChanged(material.changedAt) && (
                <Badge colorScheme="success" size="sm">
                  {t('partner.infoHub.new')}
                </Badge>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}
